import { Stack } from '@mui/material';
import { DataGridPro, DataGridProProps, GridRowHeightParams, GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { generateUUIDv4 } from '@/modules/MuiDialog/utils/uuid';
import { uniqBy, uniqWith, includes, merge, cloneDeep } from 'lodash';
import BasicGridToolBar, { Props as BasicGridToolBarProps } from './BasicGridToolBar';
import TagFields, { Props as TagProps } from './TagFields';
import ReferenceField, { ReferenceFieldProps } from './ReferenceField';
import CustomHeaderCell from './CustomHeaderCell';
import { styled } from '@mui/material';

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
    letterSpacing: 'normal',

    '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell': {
        borderRight: `1px solid ${theme.palette.mode === 'light' ? '#f0f0f0' : '#303030'}`,
    },

    '& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell': {
        borderBottom: `1px solid ${theme.palette.mode === 'light' ? '#f0f0f0' : '#303030'}`,

        '&--editable': {
            // backgroundColor: 'red',

            '&:focus, &:focus-within': {
                outline: '1px solid var(--v-palette-text-primary)',

                backgroundColor: 'var(--v-palette-action-hover)',
            },
        },
    },
}));

interface BasicDataGridProProps extends DataGridProProps {
    sortableFields?: string[];
    isAutoHeightRows?: boolean;
}
export interface BasicToolBarProps extends BasicGridToolBarProps {}
export interface TagFieldsProps extends TagProps {}

const autoHeightRowProps = {
    getRowHeight: ({ id, densityFactor }: GridRowHeightParams) => 'auto',
    getEstimatedRowHeight: () => 200,
    sx: {
        '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '4px' },
        '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '8px' },
        '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '12px' },
    },
};
const NoRowsOverlay = () => {
    return (
        <Stack height="100%" alignItems="center" justifyContent="center">
            일치하는 데이터가 없습니다.
        </Stack>
    );
};

const NoResultOverlay = () => {
    return (
        <Stack height="100%" alignItems="center" justifyContent="center">
            검색 결과가 존재하지 않습니다.
        </Stack>
    );
};
const defaultProps: BasicToolBarProps = {
    components: {
        // Toolbar: BasicGridToolBar,
        NoRowsOverlay: NoRowsOverlay,
        NoResultOverlay: NoResultOverlay,
        BooleanCellFalseIcon: () => <div></div>,
    },
};
const BasicDataGridPro = ({
    columns = [],
    rows = [],
    density = 'standard',
    hideFooter,
    disableColumnMenu = true,
    sortableFields = [],
    isAutoHeightRows = false,
    pageSizeOptions = [],
    ...props
}: BasicDataGridProProps) => {
    const autoGeneratedColumns = useMemo(() => {
        if (!!columns?.length || !rows?.length) return columns;
        const nColumns: GridColDef[] = [];
        const sampleItem = { ...rows[0] };
        Object.entries(sampleItem).forEach(([key, value]) => {
            const typeOfValue = typeof value;
            const type = ['string', 'number', 'boolean'].includes(typeOfValue) ? typeOfValue : 'string';
            nColumns.push({
                field: key,
                headerName: key,
                type: type,
                valueFormatter: (params) => {
                    const { value } = params;

                    if (typeof value === 'object') {
                        return JSON.stringify(value);
                    } else {
                        return value;
                    }
                },
                sortable: includes(sortableFields, key) || false,
                flex: 1,
            });
        });
        return nColumns;
    }, [rows, columns]);
    const computedColumns = useMemo(() => {
        const nColumns: GridColDef[] = [];
        const mainColumns: GridColDef[] = columns.filter((o) => !!o?.flex && o?.flex >= 1);

        columns.forEach((item, index) => {
            const nItem = { ...item };

            if (!nItem?.sortable) nItem.sortable = includes(sortableFields, item?.field) || false;
            if (!mainColumns?.length) nItem.flex = 1;

            nColumns.push(nItem);
        });

        return nColumns || [];
    }, [columns, sortableFields]);
    const computedRows = useMemo(() => {
        const idItems = rows.filter((o) => !!o?.id);
        const identifiedItems = uniqBy(idItems, 'id');

        // 모든 데이터가 unique 한 id를 가진 경우에는 그대로 반환한다.
        if (identifiedItems.length === rows.length) return rows;

        const nRows: any[] = [];

        rows.forEach((item, index) => {
            const nItem: Record<string, any> = { ...item };

            if (!nItem?.id) {
                nItem.instanceId = nItem?.instanceId || generateUUIDv4();
                nItem.index = index;
            }

            nRows.push(nItem);
        });

        return nRows;
    }, [rows]);
    const computedProps = useMemo(() => {
        try {
            const oProps = { ...props };

            let initProps = { ...defaultProps };

            if (!!isAutoHeightRows) {
                initProps = merge({}, defaultProps, autoHeightRowProps);
            }

            return merge({}, initProps, oProps);
        } catch (e) {
            console.error(e);
        }
    }, [props, isAutoHeightRows]);
    return (
        <>
            <StyledDataGrid
                rows={computedRows}
                columns={computedColumns?.length ? computedColumns : autoGeneratedColumns}
                getRowId={!!props?.getRowId ? props?.getRowId : (r) => r.id || r.instanceId}
                slots={
                    !!computedProps?.components
                        ? {
                              toolbar: BasicToolBar,
                              ...computedProps?.components,
                          }
                        : { toolbar: BasicToolBar }
                }
                density={density}
                localeText={{
                    toolbarExport: '다운로드',
                    toolbarExportCSV: 'CSV 파일로 다운로드',
                    toolbarColumns: '열 선택',
                    columnsPanelShowAllButton: '전체보기',
                    columnsPanelHideAllButton: '전체 숨기기',
                    columnsPanelTextFieldLabel: '열 검색',
                    columnsPanelTextFieldPlaceholder: '제목',
                    columnsPanelDragIconLabel: '열 재정렬',
                    pinToLeft: '왼쪽에 고정',
                    pinToRight: '오른쪽에 고정',
                    columnMenuManageColumns: '열 조정',
                    unpin: '고정 해제',
                    MuiTablePagination: {
                        labelRowsPerPage: '페이지당 행 수:',
                        labelDisplayedRows: ({ from, to, count }) =>
                            `${from}~${to} / 총 ${count !== -1 ? count : `${to}개 이상`}건`,
                    },
                }}
                isRowSelectable={() => false}
                pageSizeOptions={pageSizeOptions.length > 0 ? pageSizeOptions : [5, 10, 15, 25, 50, 100]}
                // TODO: 1조까지는 가능 여부 확인
                disableColumnMenu={disableColumnMenu}
                getRowClassName={
                    !!props?.getRowClassName
                        ? props.getRowClassName
                        : (params) => {
                              return !!props?.onRowClick ? 'cursor-pointer' : '';
                          }
                }
                hideFooter={hideFooter}
                sx={{
                    '& .MuiDataGrid-row': {
                        cursor: !!props?.onRowClick || !!props?.onRowDoubleClick ? 'pointer' : 'default',
                    },
                }}
                // TODO: 페이지네이션 관련 추가
                {...computedProps}
            />
            {!!hideFooter && rows.length > 100 ? <div>최대 100개의 데이터만 표시됩니다.</div> : ''}
        </>
    );
};
export const BasicToolBar = BasicGridToolBar;
export { TagFields, CustomHeaderCell, ReferenceField, type ReferenceFieldProps };

export default BasicDataGridPro;
